import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../constants/api';

const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authValue, setAuthValue] = useState(null);

  // If the currently-active token just failed (401 / expired) and it turns
  // out to have been a "ver como usuario" impersonation token (1h-lived by
  // design, see impersonate() below), fall back to the admin's own backed-up
  // session instead of wiping everything — the admin's real login is still
  // valid, only the short-lived impersonation token wasn't. Forgetting to
  // click "salir de modo vista" before that hour is up used to cost the
  // admin their whole session; now it just quietly exits impersonation.
  // Returns true if it restored a session (caller should stop there).
  const restoreAdminSessionIfImpersonating = () => {
    const adminToken = localStorage.getItem('admin_token_backup');
    const adminUser = localStorage.getItem('admin_user_backup');
    if (!adminToken || !adminUser) return false;
    localStorage.setItem('token', adminToken);
    localStorage.setItem('user', adminUser);
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('admin_user_backup');
    axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    setUser(JSON.parse(adminUser));
    return true;
  };

  // Configure axios interceptor to always include token from localStorage
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // If we get a 401, token is invalid - clear session
        if (error.response?.status === 401) {
          if (restoreAdminSessionIfImpersonating()) {
            console.log('Impersonation token expired, restored admin session');
          } else {
            // Only a real, previously-logged-in session counts as an
            // "expiration" worth explaining — a 401 from, say, a failed
            // login attempt on the login page itself hits this same branch
            // but there was no token to begin with, so hadToken stays false
            // and LoginPage won't show a confusing "session expired" banner
            // for a simple wrong-password error.
            const hadToken = !!localStorage.getItem('token');
            console.log('Received 401, clearing session');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('profile_url');
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];
            if (hadToken) {
              sessionStorage.setItem('cn_session_expired', '1');
            }
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Function to check if token is still valid
  const isTokenValid = (token) => {
    if (!token) return false;
    try {
      // Decode JWT to check expiration (simple check)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  };

  // Function to refresh user session
  const refreshUserSession = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        // Check if token is still valid
        if (isTokenValid(token)) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log('Session restored for user:', parsedUser.email);
          return true;
        } else if (restoreAdminSessionIfImpersonating()) {
          console.log('Impersonation token expired, restored admin session');
          return true;
        } else {
          // Token expired
          console.log('Token expired, clearing session');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('profile_url');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          return false;
        }
      }
      return false;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  };

  useEffect(() => {
    // Restore session on mount
    const initAuth = async () => {
      await refreshUserSession();
      setLoading(false);
    };
    initAuth();
  }, []);

  // Check token validity every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && !isTokenValid(token)) {
        if (restoreAdminSessionIfImpersonating()) {
          console.log('Impersonation token expired during session, restored admin session');
        } else {
          console.log('Token expired during session, logging out');
          logout();
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [user]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData, profile_url, needs_activation } = response.data;
      
      // Clear any previous team access data
      localStorage.removeItem('access_type');
      localStorage.removeItem('team_profile_id');
      localStorage.removeItem('team_name');
      // A real login always starts a fresh session — drop any leftover
      // "viendo como" state from a previous admin impersonation.
      localStorage.removeItem('admin_token_backup');
      localStorage.removeItem('admin_user_backup');

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      if (profile_url) {
        localStorage.setItem('profile_url', profile_url);
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      return { success: true, profile_url, needs_activation, is_admin: userData.is_admin };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      return { success: false, error: errorMessage };
    }
  };

  const register = async (name, email, password, recaptchaToken) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password, recaptcha_token: recaptchaToken });
      const { access_token, user: userData, profile_url, needs_activation } = response.data;
      
      // Clear any previous team access data
      localStorage.removeItem('access_type');
      localStorage.removeItem('team_profile_id');
      localStorage.removeItem('team_name');
      // A real login always starts a fresh session — drop any leftover
      // "viendo como" state from a previous admin impersonation.
      localStorage.removeItem('admin_token_backup');
      localStorage.removeItem('admin_user_backup');

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      if (profile_url) {
        localStorage.setItem('profile_url', profile_url);
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      return { success: true, profile_url, needs_activation, is_admin: userData.is_admin };
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Registration failed';
      return { success: false, error: errorMessage };
    }
  };

  const socialLogin = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/social-login`, { 
        session_id: sessionId 
      });
      
      const { 
        access_token, 
        user: userData, 
        profile_url, 
        session_token, 
        needs_activation, 
        activation_message 
      } = response.data;

      // A real login always starts a fresh session — drop any leftover
      // "viendo como" state from a previous admin impersonation.
      localStorage.removeItem('admin_token_backup');
      localStorage.removeItem('admin_user_backup');

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      if (profile_url) {
        localStorage.setItem('profile_url', profile_url);
      }

      // Set authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);

      return {
        success: true,
        profile_url,
        is_admin: userData.is_admin,
        user: userData,
        needs_activation,
        activation_message
      };
    } catch (error) {
      console.error('Social login error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Social login failed';
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('profile_url');
    // Clear team access data
    localStorage.removeItem('access_type');
    localStorage.removeItem('team_profile_id');
    localStorage.removeItem('team_name');
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('admin_user_backup');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Admin "Ver como usuario": swap the admin's own session for a short-lived
  // token that logs in as `userId`, stashing the admin session first so
  // stopImpersonating() can restore it. Full-page reloads on both ends of
  // this (see AdminSubscriptionsTab / the impersonation banner) so every
  // component remounts under the right identity instead of carrying over
  // stale state from whoever was logged in a moment ago.
  const impersonate = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/impersonate`);
      const { access_token, user: userData } = response.data;

      const currentToken = localStorage.getItem('token');
      const currentUser = localStorage.getItem('user');
      if (currentToken && currentUser) {
        localStorage.setItem('admin_token_backup', currentToken);
        localStorage.setItem('admin_user_backup', currentUser);
      }

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'No se pudo iniciar sesión como este usuario';
      return { success: false, error: errorMessage };
    }
  };

  const stopImpersonating = () => {
    const adminToken = localStorage.getItem('admin_token_backup');
    const adminUser = localStorage.getItem('admin_user_backup');
    if (!adminToken || !adminUser) return;

    localStorage.setItem('token', adminToken);
    localStorage.setItem('user', adminUser);
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('admin_user_backup');
    axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    setUser(JSON.parse(adminUser));
  };

  const isImpersonating = !!localStorage.getItem('admin_token_backup');

  // Patch fields onto the in-memory user + localStorage cache without a
  // backend round-trip — for callers that already persisted the change
  // server-side and just need the local session to reflect it immediately
  // (e.g. suppressing the tutorial right after it's marked complete).
  const updateUser = (fields) => {
    setUser((prev) => {
      const next = { ...prev, ...fields };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  // Starts a session from an access_token the backend already issued (e.g.
  // after a successful password reset) — same storage/header/state as
  // login()/register(), just without the extra round-trip since the token
  // is already in hand.
  const loginWithToken = (access_token, userData) => {
    localStorage.removeItem('access_type');
    localStorage.removeItem('team_profile_id');
    localStorage.removeItem('team_name');
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('admin_user_backup');

    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));

    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
  };

  const refreshUser = async () => {
    // Refresh user data from backend after activation or profile changes
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      
      const response = await axios.get(`${API}/profiles/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data) {
        const userData = response.data;
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return false;
    }
  };

  // Memoize context value to prevent unnecessary re-renders
  React.useEffect(() => {
    setAuthValue({ user, login, register, socialLogin, logout, refreshUser, updateUser, loading, impersonate, stopImpersonating, isImpersonating, loginWithToken });
  }, [user, loading]);

  if (!authValue) {
    return null;
  }

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
